import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import SearchBox from '../../../components/common/Search/SearchBox';
import Filter from '../../../components/common/Filter/Filter';
import GroupBy from '../../../components/common/GroupBy/GroupBy';
import Sort from '../../../components/common/Sort/Sort';
import TotalCount from '../../../components/common/TotalCount/TotalCount';
import ArchivePopup from '../../../components/Popup/ArchivePopup/ArchivePopup';
import ArchiveIcon from '../../../components/Table/ArchiveIcon/ArchiveIcon';
import EditIcon from '../../../components/Table/EditIcon/EditIcon';
import AddButton from '../../../components/common/AddButton/AddButton';
import ArchiveSelectedItem from '../../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import '../../Faq/Faqs.css';
import { baseurl } from '../../../api';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import { useCheckboxClick } from '../../../checkboxclick';
import authenticatedAxios from '../../../authenticatedAxios';
import useFilterStore from '../../../store/useFilterStore';
import DeletePopup from '../../../components/Popup/DeletePopup/DeletePopup';
import DeleteIcon from '../../../components/Table/DeleteIcon/DeleteIcon';
import useRefreshStore from '../../../store/useRefreshStore';
import { archiveOrgUser, archiveSelectedOrgUsers, deleteOrgUser } from '../../../services/Subscriptions/OrgUserService';
import Pagination from '../../../components/common/Pagination/pagination';

const pageKey = "useraccounts";

const UserAccounts = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: "" });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Unarchived");
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");
  const sortOptions = ['A-Z', 'Z-A', 'Newest', 'Oldest'];
  const searchCategories = ['Name', 'Company', 'Mobile', 'Email'];
  const filterOptions = ['Default', 'Archived', 'Unarchived'];
  const groupbyOptions = ['Default', 'Company'];
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [groupedItems, setGroupedItems] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedGroupByOption, setSelectedGroupByOption] = useState('Default');
  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange, resetCheckboxState, } = useCheckboxClick(items);
  const { refreshKeys } = useRefreshStore();
  const refreshKey = refreshKeys["User Accounts"] || 0;
  const [pagination, setPagination] = useState({});
  const [totalCount, setTotalCount] = useState(0); 

  const fetchUseraccounts = async (id = null, page = null) => {
    try {
      if (id) {
        const response = await authenticatedAxios.get(`${baseurl}accounts/users/organizational/?id=${id}`);

        if (response?.data?.response_code === 1) {
          setSelectedItem(response.data.data);
        } else {
          console.error("No data found for the selected user account.");
        }
      } else {
        const params = {
          search: searchParams.term,
          search_argument: searchParams.argument,
          filter: selectedFilterOption === 'Default' ? undefined : selectedFilterOption.toLowerCase(),
          sort: mapSortOptionToQuery(selectedSortOption),
          group_by: selectedGroupByOption.toLowerCase(),
          ...(page && { page })
        };

        const response = await authenticatedAxios.get(`${baseurl}accounts/users/organizational/`, { params });

        if (response?.data?.response_code === 1) {
          setItems(response.data.data);
          if (response.data.meta?.total_count !== undefined) {
            setTotalCount(response.data.meta.total_count);
          }
          if (selectedGroupByOption !== 'Default') {
            groupItems(response.data.data, selectedGroupByOption);
          }
          if (response.data.pagination) {
            setPagination(response.data.pagination);
          }
        } else {
          console.error("Unexpected response code or no data found for user accounts.");
        }
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      } else {
        console.error('Error fetching user accounts:', error);
      }
    }
  };

  const groupItems = (data, groupBy) => {
    const grouped = data.reduce((acc, item) => {
      let key;
      switch (groupBy.toLowerCase()) {
        case 'company':
          key = item.organization?.name || 'Unknown';
          break;
        default:
          key = 'default';
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    setGroupedItems(grouped);
  };

  useEffect(() => {
    fetchUseraccounts();
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption,
    });
  }, [searchParams, selectedFilterOption, selectedSortOption, selectedGroupByOption]);

  useEffect(() => {
    fetchUseraccounts();
  }, [refreshKey]);

  const handleGroupBySelect = (option) => {
    setSelectedGroupByOption(option);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const renderGroupedTable = () => {
    if (selectedGroupByOption === 'Default') {
      return (
        <tbody className='common-table-tbody'>
          {renderTableRows(items)}
        </tbody>
      );
    }

    return (
      <tbody className='common-table-tbody'>
        {Object.entries(groupedItems).map(([groupName, groupSubscriptions]) => (
          <React.Fragment key={groupName}>
            <tr className="group-header-row">
              <td colSpan="6" className="group-header">
                <div
                  className="group-header-content"
                  onClick={() => toggleGroup(groupName)}
                >
                  <svg
                    className={`expand-item-icon ${expandedGroups.has(groupName) ? 'rotated' : ''}`}
                    width="7"
                    height="13"
                    viewBox="0 0 7 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 6.5L0.25 12.9952L0.250001 0.00480908L7 6.5Z" fill="#424242" />
                  </svg>
                  <span className="group-name common-text fts-15 blue">{groupName}</span>
                  <span className="group-count common-text fts-15 blue">({groupSubscriptions.length})</span>
                </div>
              </td>
            </tr>
            {expandedGroups.has(groupName) && renderTableRows(groupSubscriptions)}
          </React.Fragment>
        ))}
      </tbody>
    );
  };

  const renderTableRows = (useraccounts) => {
    return useraccounts.length > 0 ? (
      useraccounts.map((useraccount) => (
        <tr key={useraccount.id} className={useraccount.is_archived ? 'archived-item' : ''}>
          <td className='d-flex ai-center'>
            <div className='d-flex ai-center'>
              <input
                className="table-selectbox table-child-selectbox"
                type="checkbox"
                checked={selectedItems.includes(useraccount.id)}
                onChange={() => handleChildCheckboxChange(useraccount.id)}
              />
            </div>
            <span onClick={() => handleUserDetailClick(useraccount.id)} className="blue company-name">{useraccount.full_name}</span>
          </td>
          <td className='blue cr-pointer' onClick={() => handleDetailClick(useraccount.organization?.id)}>
            {useraccount.organization?.name}
          </td>
          <td className='txt-lc'>{useraccount.email}</td>
          <td>{useraccount.country_code?.country_code} {useraccount.phone}</td>
          <td>{useraccount.role?.name}</td>

          <td className="actions common-action">
            <EditIcon onClick={() => handleEditClick(useraccount)} />
            <ArchiveIcon onClick={() => handleArchiveClick(useraccount)} isArchived={useraccount.is_archived} />
            <DeleteIcon onClick={() => handleDeleteClick(useraccount)} />
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="6">No users found.</td>
      </tr>
    );
  };

  const handleArchiveSelectedItems = async () => {
    const result = await archiveSelectedOrgUsers(selectedItems);
    if (result.success) {
      fetchUseraccounts();
      resetCheckboxState();
      closePopups();
    }
  };

  const handleArchive = async () => {
    const result = await archiveOrgUser(selectedItem.id);
    if (result.success) {
      await fetchUseraccounts();
      closePopups();
    }
  };

  const handleDelete = async () => {
    const result = await deleteOrgUser(selectedItem.id);
    if (result.success) {
      closePopups();
      fetchUseraccounts();
    }
  };

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z':
        return 'a_to_z';
      case 'Z-A':
        return 'z_to_a';
      case 'Newest':
        return 'newest';
      case 'Oldest':
        return 'oldest';
      default:
        return 'newest';
    }
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
  };

  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  const handleSearchSelect = (searchCategory, searchTerm) => {
    setSearchParams({
      term: searchTerm,
      argument: searchCategory.toLowerCase(),
    });
  };

  const handlePageChange = (page) => {
    fetchUseraccounts(null, page);
  };

  const handleArchiveClick = async (item) => {
    setSelectedItem(item);
    setIsArchivePopupOpen(true);
  };

  const handleEditClick = (item) => {
    navigate(`/user-accounts/edit/${item.id}`);
  };

  const handleDeleteClick = async (item) => {
    setSelectedItem(item);
    setIsDeletePopupOpen(true);
  };

  const handleNewClick = () => {
    navigate('/user-accounts/create');
  };

  const handleUserDetailClick = (userId) => {
    navigate(`/user-accounts/detail/${userId}`);
  };

  const handleDetailClick = (organizationId) => {
    if (organizationId) {
      navigate(`/customer-detail/${organizationId}`);
    } else {
      console.error('Organization ID is undefined');
    }
  };

  const deletePopupFields = [
    { label: 'Are you sure you want to delete this record? Once deleted, it cannot be recovered.', type: 'text' },
  ];

  const handleExportComplete = () => {
    resetCheckboxState();
  };

  const closePopups = () => {
    setIsArchivePopupOpen(false);
    setIsDeletePopupOpen(false);
    setSelectedItem([]);
  };

  return (
    <div className='whole-container'>
      <div className='top-heading'>
        <PageHeader pageName="Subscription /" nestedpageName="User Accounts" linkTo="" />
        <div className='export-container'>
          <ArchiveSelectedItem
            selectedItems={selectedItems}
            items={items}
            onArchiveSelected={handleArchiveSelectedItems}
          />
          <ExportLabel
            exportUrl={`${baseurl}accounts/organizational/users/export/`}
            selectedIds={selectedItems}
            onExportComplete={handleExportComplete}
          />
        </div>
      </div>
      <div className='pagename-container'>
        <div className='page-name'>
          <h1 className='common-text fts-22 ftw-6 blue'>User Accounts</h1>
        </div>
        <div className='pagename-right-container'>
          <TotalCount TotalName="Total User Accounts" TotalCount={totalCount} />
          <AddButton onClick={handleNewClick} />
          <SearchBox categories={searchCategories} onSearchSelect={handleSearchSelect} defaultValue={searchParams?.term} />
          <GroupBy
            options={groupbyOptions}
            selectedOption={selectedGroupByOption}
            onSelect={handleGroupBySelect}
          />
          <Filter options={filterOptions} onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption} />
          <Sort options={sortOptions} onOptionSelect={handleSortOptionSelect} defaultValue={selectedSortOption} />
        </div>
      </div>
      <div className='table-container'>
        <table className='common-table'>
          <thead className='common-table-head'>
            <tr className='common-table-thead-tr'>
              <th className='w-20'>
                <input
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />Name
              </th>
              <th className='w-20'>Company</th>
              <th className='w-20'>Email</th>
              <th className='w-15'>Mobile</th>
              <th className='w-15'>Role</th>
              <th className='w-10'>Actions</th>
            </tr>
          </thead>
          {renderGroupedTable()}
        </table>
      </div>

      {pagination && pagination.total_pages > 1 && (
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          totalCount={pagination.total_items}
          perPage={pagination.items_per_page}
          onPageChange={handlePageChange}
        />
      )}

      <ArchivePopup
        isOpen={isArchivePopupOpen}
        isArchived={selectedItem?.is_archived}
        archivename={selectedItem?.full_name}
        onSave={handleArchive}
        onClose={() => setIsArchivePopupOpen(false)}
      />
      <DeletePopup
        isOpen={isDeletePopupOpen}
        title="Delete User"
        fields={deletePopupFields}
        onSave={handleDelete}
        onClose={closePopups}
      />
    </div>
  );
};

export default UserAccounts;
